import React, { useEffect } from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import Hero from '@/components/Hero';
import ContactForm from '@/components/ContactForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { CheckCircle2, BarChart3, Bot, Share, Database, Target, PieChart, BarChart, TrendingUp, LineChart } from 'lucide-react';
import ServicesSection from '@/components/ecommerce-automation/ServicesSection';

const EcommerceAutomation = () => {
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.reveal').forEach(el => {
      el.classList.add('opacity-0');
      observer.observe(el);
    });

    return () => {
      document.querySelectorAll('.reveal').forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Ecommerce Automation | Stratodata"
        description="Automate your ecommerce operations — from product management to pricing and reporting. We build the data pipelines that run your retail business on autopilot."
        canonical="/services/ecommerce-automation"
      />
      <Navigation />
      
      <Hero
        title="Ecommerce Automation Suite"
        subtitle="Your core reports and automations in one place. Managed from an unified UI, which provides reporting and management capabilities for guidance of business process automations."
        ctaText="Get Started"
        ctaLink="#pricing"
        showSwitchingText={true}
      />
      
      <section className="py-16 md:py-24 bg-secondary/10">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                Core Principles
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                Data-Driven Automation for Ecommerce
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                This suite has been developed by minding the following principles:
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Profitability is Key</h3>
                    <p className="text-muted-foreground text-sm">
                      All solutions are aimed to help you manage and grow your profitability and invest where the ROI is.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                    <BarChart className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Reports are Just the Beginning</h3>
                    <p className="text-muted-foreground text-sm">
                      Real power of data comes alive when you put it to work.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                    <Bot className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Automate Data Collection</h3>
                    <p className="text-muted-foreground text-sm">
                      Automate your data collection and reporting activities with this suite. Put your time and energy into activities which move your business forward.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                    <Database className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Optimized Warehouse Management</h3>
                    <p className="text-muted-foreground text-sm">
                      An optimized warehouse is very important for your profitability and cash. This suite will help you manage stock sellout.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                    <Share className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">One-Stop Solution</h3>
                    <p className="text-muted-foreground text-sm">
                      One stop solution for all product and ecommerce campaign management data. Look no further.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="reveal">
              <div className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden relative">
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-secondary/10 to-transparent"></div>
                <div className="p-8 relative z-10">
                  <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="h-24 w-24 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                        <BarChart3 className="h-12 w-12 text-primary" />
                      </div>
                      <h4 className="text-lg font-medium text-center">Advanced Reporting</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="h-24 w-24 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                        <Bot className="h-12 w-12 text-primary" />
                      </div>
                      <h4 className="text-lg font-medium text-center">Smart Automation</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="h-24 w-24 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                        <Target className="h-12 w-12 text-primary" />
                      </div>
                      <h4 className="text-lg font-medium text-center">Campaign Management</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="h-24 w-24 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                        <PieChart className="h-12 w-12 text-primary" />
                      </div>
                      <h4 className="text-lg font-medium text-center">Product Management</h4>
                    </div>
                  </div>
                  
                  <div className="mt-8 text-center">
                    <p className="text-primary font-medium">More coming soon, long road ahead</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Our Solutions
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              Comprehensive Suite of Tools
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              This suite contains solutions coming from the Stratodata team's previous experience with delivery of custom solutions in this segment.
            </p>
          </div>
          
          <Tabs defaultValue="reporting" className="reveal">
            <TabsList className="w-full grid grid-cols-2 max-w-md mx-auto mb-8">
              <TabsTrigger value="reporting">Reporting</TabsTrigger>
              <TabsTrigger value="automation">Automation</TabsTrigger>
            </TabsList>
            
            <TabsContent value="reporting" className="space-y-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center mb-2">
                    <div className="p-2 mr-4 bg-primary/10 rounded-lg">
                      <LineChart className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle>Ecommerce Campaign Management Report</CardTitle>
                  </div>
                  <CardDescription>
                    Integrated marketing, web and sales data for optimal decision making
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Data on marketing, web and internal sales and margin data integrated</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Organized by your own custom channel and platform mapping</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Daily performance of your sites and channels, enable you to make the right decisions over and over</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Maximize growth while controlling profitability on campaign level</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center mb-2">
                    <div className="p-2 mr-4 bg-primary/10 rounded-lg">
                      <BarChart className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle>Product Management Control Panel</CardTitle>
                  </div>
                  <CardDescription>
                    Comprehensive product performance and stock management solution
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Combination of sales & stock metrics, core product management KPIs as WOH calculated</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Data presented on own product catalogue, KPIs drillable on all levels (from EAN all the way to top category or brand)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Marketing profitability as key profit metric, lower levels of profitability available optionally</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Visibility on web and PPC data, competitor prices can be included</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>All data organized in one management dashboard with daily data freshness</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="automation" className="space-y-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center mb-2">
                    <div className="p-2 mr-4 bg-primary/10 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle>Autopricing</CardTitle>
                  </div>
                  <CardDescription>
                    Automated pricing algorithm to optimize your product prices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-muted-foreground">
                    Automated pricing algorithm which sets prices of your products. The suite offers off the shelf pre-made autopricing logic, which utilizes connected data from marketing, web and ERP. Customizations on this base logic can be done per your request.
                  </p>
                  
                  <h4 className="font-medium mb-2">The off-the-shelf autopricing logic utilizes:</h4>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>History of product sales</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Current product stock</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Marketing performance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Web performance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>Competitor prices (optional)</span>
                    </li>
                  </ul>
                  
                  <p className="text-muted-foreground">
                    Management interface is part of the solution. This enables you to manage the automated solution - work is done by the data, but you stay in the driver seat.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center mb-2">
                    <div className="p-2 mr-4 bg-primary/10 rounded-lg">
                      <Target className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle>Product Bidder</CardTitle>
                  </div>
                  <CardDescription>
                    Automatically optimize your PPC campaigns based on product performance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-muted-foreground">
                    Thanks to the advanced data integration this suite offers, you can take advantage of automated product sorting into PPC campaigns. Invest where the ROI is, lay out your products across various campaigns in a pre-defined manner and take control of the PPC machine.
                  </p>
                  
                  <h4 className="font-medium mb-2">Use cases include:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>
                        <span className="font-medium">Product promotion</span> - product is selling, but it is receiving sub-par exposure. Move to more aggressive campaign to drive additional traffic and ultimately sales.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span>
                        <span className="font-medium">Product exclusion</span> - product profitability is very low. Investment goes in the wrong direction, we are burning money. Let's hold it back.
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      <ServicesSection />
      
      <section className="py-16 md:py-24 bg-white">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Data Dashboard
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              Interactive Ecommerce Analytics
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-10">
              Explore our interactive dashboard with real-time data visualization for ecommerce performance metrics.
            </p>
          </div>
          
          <div className="reveal shadow-xl rounded-xl overflow-hidden border border-border">
            <iframe 
              title="ecom_template_ver01" 
              width="100%" 
              height="600" 
              src="https://app.powerbi.com/view?r=eyJrIjoiNGJkOTMzN2QtMjEwZC00NzY4LWIyNmEtMjFhNTU2OTQxMjFkIiwidCI6IjJkZTM4MDIyLTY1YWQtNGJlZi05MmJmLWZlMmZkYzU5MzBlMyIsImMiOjl9" 
              frameBorder="0" 
              allowFullScreen={true}
              className="w-full"
            ></iframe>
          </div>
        </div>
      </section>
      
      <section id="pricing" className="py-16 md:py-24 bg-secondary/10">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We offer flexible pricing tiers to suit businesses of all sizes. Start with the plan that fits your current needs and scale up as you grow.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 reveal">
            <Card className="border-border/40 relative">
              <CardHeader>
                <CardTitle>Starter</CardTitle>
                <CardDescription>Up to 5k orders / month</CardDescription>
                <div className="mt-4">
                  <div className="text-3xl font-bold">€999</div>
                  <div className="text-muted-foreground text-sm">per month</div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col h-full">
                <ul className="space-y-2 mb-8 flex-grow">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>2 standard reports</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>2 standard automations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Connection of marketing, web and ERP data</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Email support</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full mt-auto">Contact Sales</Button>
              </CardContent>
            </Card>
            
            <Card className="border-border/40 relative">
              <CardHeader>
                <CardTitle>Grower</CardTitle>
                <CardDescription>Up to 15k orders / month</CardDescription>
                <div className="mt-4">
                  <div className="text-3xl font-bold">€1,599</div>
                  <div className="text-muted-foreground text-sm">per month</div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col h-full">
                <ul className="space-y-2 mb-8 flex-grow">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Customizable reports</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Priority email support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Quarterly strategy calls</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>All Starter features</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full mt-auto">Contact Sales</Button>
              </CardContent>
            </Card>
            
            <Card className="border-border/40 relative">
              <div className="absolute top-0 right-0 bg-primary text-white text-xs font-medium px-3 py-1">
                Popular
              </div>
              <CardHeader>
                <CardTitle>Shower</CardTitle>
                <CardDescription>Up to 35k orders / month</CardDescription>
                <div className="mt-4">
                  <div className="text-3xl font-bold">€2,899</div>
                  <div className="text-muted-foreground text-sm">per month</div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col h-full">
                <ul className="space-y-2 mb-8 flex-grow">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Premium reporting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Dedicated account manager</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Monthly strategy calls</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Usage metrics available</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>All Grower features</span>
                  </li>
                </ul>
                <Button className="w-full mt-auto">Contact Sales</Button>
              </CardContent>
            </Card>
            
            <Card className="border-border/40 relative">
              <CardHeader>
                <CardTitle>Enterprise</CardTitle>
                <CardDescription>Unlimited orders</CardDescription>
                <div className="mt-4">
                  <div className="text-3xl font-bold">Custom</div>
                  <div className="text-muted-foreground text-sm">contact for pricing</div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col h-full">
                <ul className="space-y-2 mb-8 flex-grow">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Fully tailored to your needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>All Shower features</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full mt-auto">Contact Sales</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      <section id="contact" className="py-16 md:py-24 bg-background relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-secondary/20"></div>
        
        <div className="container max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="reveal">
              <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                Get Started
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                Ready to Transform Your Ecommerce Operations?
              </h2>
              <p className="text-muted-foreground mb-8">
                Contact us today to discuss how our Ecommerce Automation Suite can help you achieve better profitability and growth. Our team of experts is ready to guide you through the implementation process.
              </p>
              
              <div className="p-6 bg-white/50 backdrop-blur-sm border border-border shadow-sm rounded-lg mb-8">
                <h3 className="font-medium text-lg mb-2">Why Choose Our Automation Suite?</h3>
                <p className="text-muted-foreground mb-4">
                  We are very much in development, so expect other cool features to arrive soon! Cheers to the datadriven future!
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Proven results with ecommerce retailers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Continuous development of new features</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span>Expert support throughout implementation and beyond</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="reveal">
              <ContactForm service="Ecommerce Automation Suite" />
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default EcommerceAutomation;
